"use client";

import { useEffect, useRef, useState } from "react";

const HEIGHT_MESSAGE = /^\d{1,5}(?:\.\d+)?px$/;

/** Indlejret side's frame. With `resize` the provider's own height messages set its height; every other message
 *  (LeadDoubler's "init-session", which asks the parent for document.cookie, and its tracking objects) is ignored and
 *  nothing reaches the site's tag manager. */
export default function EmbedFrame({
  src,
  title,
  resize,
}: {
  src: string;
  title: string;
  resize: boolean;
}) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number>();
  useEffect(() => {
    if (!resize) return;
    const origin = new URL(src, window.location.href).origin;
    let frame = 0;
    const listen = (event: MessageEvent) => {
      if (
        event.source !== ref.current?.contentWindow ||
        event.origin !== origin ||
        typeof event.data !== "string" ||
        !HEIGHT_MESSAGE.test(event.data)
      )
        return;
      const next = Math.min(
        5000,
        Math.max(150, Math.ceil(Number.parseFloat(event.data))),
      );
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setHeight(next));
    };
    window.addEventListener("message", listen);
    // A page that loaded before hydration posted its height before anyone listened. The providers post again on
    // every resize, so the frame grows by 1 px for two rendering updates.
    const element = ref.current;
    let nudge = 0;
    if (element) {
      const nudged = `${element.getBoundingClientRect().height + 1}px`;
      element.style.height = nudged;
      nudge = requestAnimationFrame(() => {
        nudge = requestAnimationFrame(() => {
          if (element.style.height === nudged) element.style.height = "";
        });
      });
    }
    return () => {
      window.removeEventListener("message", listen);
      cancelAnimationFrame(frame);
      cancelAnimationFrame(nudge);
    };
  }, [src, resize]);
  return (
    <iframe
      ref={ref}
      src={src}
      title={title}
      loading="lazy"
      referrerPolicy="strict-origin-when-cross-origin"
      sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      className="pf-embed-frame"
      style={height ? { height: `${height}px` } : undefined}
      data-resize={resize ? "px-message" : undefined}
      data-auto-height={height ? "true" : undefined}
    />
  );
}
